import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Formik, Form } from 'formik'
import { authAPI } from '../services/api'
import { Button, FormikInput, Alert } from '../components/ui'
import { forgotPasswordSchema } from '../utils/validationSchemas'

const ForgotPasswordPage = () => {
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [error, setError] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (values, { setSubmitting }) => {
    setIsLoading(true)
    setError(null)

    try {
      const response = await authAPI.forgotPassword(values.email)

      if (response.data.success) {
        setIsSubmitted(true)
      } else {
        setError(response.data.message || 'Failed to send reset email')
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.errors?.email?.[0] ||
        'An error occurred. Please try again.'
      )
    } finally {
      setIsLoading(false)
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo/Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">LinkBoard</h1>
            <p className="text-gray-600">Reset your password</p>
          </div>

          {isSubmitted ? (
            <div className="text-center">
              <div className="mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Check your email</h2>
              <p className="text-gray-600 mb-6">
                If an account exists with that email, we've sent a password reset link.
              </p>
              <Link to="/login">
                <Button variant="primary" className="w-full">
                  Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <>
              {/* Error Message */}
              {error && (
                <div className="mb-4">
                  <Alert variant="danger" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                </div>
              )}

              {/* Info Message */}
              <div className="mb-6">
                <Alert variant="info">
                  Enter your email address and we'll send you a link to reset your password.
                </Alert>
              </div>

              {/* Forgot Password Form */}
              <Formik
                initialValues={{ email: '' }}
                validationSchema={forgotPasswordSchema}
                onSubmit={handleSubmit}
                enableReinitialize={false}
              >
                {({ isSubmitting }) => (
                  <Form className="space-y-6" noValidate>
                    <FormikInput
                      id="email"
                      name="email"
                      label="Email Address"
                      type="email"
                      placeholder="Enter your email address"
                      disabled={isLoading || isSubmitting}
                    />

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      loading={isLoading || isSubmitting}
                      className="w-full"
                    >
                      Send Reset Link
                    </Button>
                  </Form>
                )}
              </Formik>

              <div className="mt-6 text-center">
                <Link to="/login" className="text-sm text-blue-600 hover:text-blue-700">
                  Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default ForgotPasswordPage

